import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import i18next from 'i18next';
import axios from 'axios';
import { toastDefault } from '../../utils/constants';
import { parseJsonRecovering } from '../../utils/jsonRecovery';
import { CustomPropertyDataType_LABELS } from '../../../custom-properties/utils/constants';
import type { ISelectionZoneProps } from '@fluentui/react/lib/Selection';

/**
 * How the cell being edited was opened: from the keyboard (Enter or F2 on it) or by the mouse. A cell opened from the
 * keyboard gets focus back when it closes, so the agent can carry on with the arrows - and the browser draws its focus
 * box; one opened by a click does not, so Escape leaves nothing behind (Shaun, 2026-10-19). Watched on the document,
 * since every kind of cell opens the same two ways.
 */
let openedByKeyboard = false;
if (typeof document !== 'undefined') {
  const onCell = (target: EventTarget | null) => !!(target as HTMLElement)?.closest?.('.inline-edit-cell');
  document.addEventListener('pointerdown', event => { if (onCell(event.target)) openedByKeyboard = false; }, true);
  document.addEventListener('keydown', event => {
    if ((event.key === 'Enter' || event.key === 'F2') && (event.target as HTMLElement)?.classList?.contains('inline-edit-cell')) openedByKeyboard = true;
  }, true);
}
/**
 * Puts focus back on a cell as its editor closes from the keyboard. One opened by a click is focused quietly - no box
 * (`data-quiet-focus`, main.css) - because left with nothing focused, Fluent's list moved focus to the whole row and
 * drew its outline over the cell's save marks (Shaun, 2026-10-20).
 */
export const focusCellBack = (cell: HTMLElement | null | undefined) => {
  if (!cell) return;
  if (!openedByKeyboard) {
    cell.setAttribute('data-quiet-focus', '');
    cell.addEventListener('blur', () => cell.removeAttribute('data-quiet-focus'), { once: true });
  }
  cell.focus();
};

/** A save turned down for a reason the agent should read (a closed ticket, say) - its message is shown as is. */
export class InlineEditRefused extends Error {}

const showSaveFailedToast = () => toast.error(i18next.t('ticket-details.edit-ticket-view.failed-to-update-the-ticket'), toastDefault);

/**
 * The browser gave up waiting (platformService's timeout - 40s for BulkEditTickets, 5 minutes otherwise). That
 * stops nothing on the server, which may still have saved - so it is not a failure until the row says so.
 * axios 0.21 calls both a timeout and an abort ECONNABORTED; only the timeout's message names it.
 */
export const isSaveTimeout = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.code === 'ECONNABORTED' && /timeout/i.test(error.message);

/**
 * A save that threw: the toast says why, and the outcome says what the cell shows - a refusal (closed, merged,
 * already decided) cannot be retried, anything else can. A timeout has no answer yet, for a caller whose cell can
 * check (`timeout` given): `timeout.reload` fetches the row - left out where the caller reloads anyway - and the
 * cell's mark decides from it (useSaveMark, 'unconfirmed'). Without it a timeout is a failure, as before.
 */
export const reportFailure = (error: unknown, timeout?: { reload?: () => void | Promise<void> }): SaveOutcome => {
  console.error('Inline cell save failed', error);
  if (timeout && isSaveTimeout(error)) {
    const { reload } = timeout;
    if (reload) Promise.resolve().then(reload).catch((reloadError: unknown) => console.error('Reload after an inline save timed out failed', reloadError));
    return 'unconfirmed';
  }
  if (error instanceof InlineEditRefused) toast.error(error.message, toastDefault);
  else showSaveFailedToast();
  return error instanceof InlineEditRefused ? 'refused' : 'failed';
};

/**
 * On a cell's panel, which is portalled to the end of the page: Fluent's FocusZone (the grid's and each row's) skips
 * events from inside an element carrying this, as it does for Fluent's own pop-ups. React still hands the panel's
 * events up to it, and its capture-phase mousedown ran before the panel could stop them - a click on a box's chevron
 * made the chevron the row's roving Tab stop (tabIndex 0), so after picking, Tab landed on the X that replaced it.
 */
// data-inline-panel: an open panel counts as editing, so the lists' auto refresh waits for it (editingNow.ts).
export const PANEL_PORTAL = { 'data-portal-element': 'true', 'data-inline-panel': 'true' };

/**
 * For a list with editable cells: Fluent's SelectionZone, for 100ms after any key press inside the list, selects the
 * row of whatever takes focus (isSelectedOnFocus), whatever data-selection-disabled says. Enter / Escape close a cell
 * and hand focus back to it ~26ms later, so the row was selected - Edit / Delete appeared (#20). Off, arrowing between
 * rows no longer selects the row landed on; Space and the checkbox still do.
 */
export const NO_SELECT_ON_FOCUS = { isSelectedOnFocus: false } as ISelectionZoneProps;

/**
 * Covers the whole grid cell (a positioned box), padded as the cell pads its own content so nothing moves:
 * the hover outline and the edit box are the cell's edges, not the text's. Shared by the ticket list's
 * editable cells, which also share the `inline-edit-cell` hover style in main.css.
 */
export const CELL_BOX: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  padding: '0 8px 0 12px'
};

/**
 * How a save ended. Saved: the green box fades. Failed (a save that could work - network, server): the editor
 * reopens with what was entered, boxed red. Refused (it cannot work - closed, merged, already decided) or partial
 * (part of it saved): the row shows what the server holds, boxed red. Cancelled (a dialog backed out of): as before.
 * Unconfirmed (the browser timed out): the row shows what the server holds, still saving, until the reloaded row
 * says whether the value landed. Errors keep their toast; success has none.
 */
export type SaveOutcome = 'saved' | 'failed' | 'refused' | 'partial' | 'cancelled' | 'unconfirmed';

export type SaveMark = 'saved' | 'failed' | 'refused' | null;

/** As long as the saved cell's green (inline-mark-saved-settle in main.css); a mirrored green has faded by then. */
const SAVED_MARK_MS = 4000;

/** How long a timed-out save waits for its reloaded row before it is called failed. */
const CONFIRM_MS = 15000;

/**
 * Timed-out saves still waiting for their row, by field and row. Kept outside the cells: the lists key their rows
 * by position, so a reload that reorders them hands this ticket's row to another cell - measured on My Group Work,
 * where the waiting cell was given another ticket and dropped the wait, with no red ever shown. The cell now
 * showing the ticket takes the wait up.
 */
const waitingSaves = new Map<string, { start: unknown; deadline: number }>();

/**
 * When each field of each row went green, by field and row. Fluent draws the rows in blocks of 10 and builds a row
 * again when it moves up into the block above - as a row above leaves the view - so the cell that shows the ticket
 * then picks the green up where it was rather than losing it.
 */
const greenSince = new Map<string, number>();
/** How far into its green this field of this row is, or undefined when it shows none. */
const greenElapsed = (key: string | null, now = Date.now()): number | undefined => {
  const since = key ? greenSince.get(key) : undefined;
  if (since === undefined) return undefined;
  if (now - since >= SAVED_MARK_MS) {
    greenSince.delete(key);
    return undefined;
  }
  return now - since;
};

/**
 * The cell last saved on this page, by field and row, so it keeps a quiet box once its green has gone and the agent
 * can see what they just changed (Shaun). One at a time: opening any cell takes it off. It is kept against the page's
 * address, so it goes when the agent leaves the page or switches view, and follows its ticket to whichever cell shows
 * it, as a timed-out save's wait does (waitingSaves).
 */
let lastEdited: { key: string; where: string } | null = null;
const lastEditedListeners = new Set<() => void>();
const currentAddress = () => `${window.location.pathname}${window.location.search}`;
const setLastEdited = (key: string | null) => {
  lastEdited = key ? { key, where: currentAddress() } : null;
  lastEditedListeners.forEach(listener => listener());
};
/** Whether this field of this row is the one last saved on this page. */
export const isLastEdited = (key: string | null): boolean =>
  !!key && lastEdited?.key === key && lastEdited.where === currentAddress();
/**
 * Gives the box to another cell of the row - for a panel or dialog opened from one cell that changed another
 * (Team / Group / Assignee): the box goes on the value that changed (Shaun). Call it before that cell shows its green,
 * so its green settles into the box.
 */
export const markLastEdited = (field: string, rowKey: number | string) => setLastEdited(`${field}:${rowKey}`);
/** For tests. */
export const clearLastEdited = () => setLastEdited(null);

/**
 * The green or red box on a cell after a save. Green fades by itself; red stays until a save of that cell
 * succeeds - and a refusal's red also goes when the cell is next opened, since there is nothing to fix there.
 *
 * `rowValue` is what the row holds for this cell, as something `===` can compare (a string for a list), and
 * `saving` is the cell's own save in flight. A timed-out save ('unconfirmed') is green if the reloaded row's
 * value moves off what it was when the save started - only a save or a reload puts a value in the row - and red
 * if it has not within CONFIRM_MS. `busy` covers both waits: the cell pulses and cannot be opened. `field` names
 * the column, so a wait follows its ticket (waitingSaves).
 */
export const useSaveMark = (rowKey: number | string, rowValue?: unknown, saving = false, field?: string) => {
  const waitKey = field ? `${field}:${rowKey}` : null;
  // A cell built for a ticket whose green is still running shows it from the first draw (greenSince).
  const [mark, setMark] = useState<SaveMark>(() => (greenElapsed(waitKey) !== undefined ? 'saved' : null));
  /** How far into the green the cell picked it up, so its animation carries on from there. */
  const [greenOffset, setGreenOffset] = useState(() => greenElapsed(waitKey) ?? 0);
  const [confirming, setConfirming] = useState(false);
  const [, lastEditedChanged] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const confirmTimer = useRef<ReturnType<typeof setTimeout>>();
  const startValue = useRef<unknown>(rowValue);
  const rowValueRef = useRef(rowValue);
  rowValueRef.current = rowValue;
  /** Set by begin(): the start value is already noted, before the cell put its new value into the row. */
  const begun = useRef(false);

  const giveUp = () => {
    if (waitKey) waitingSaves.delete(waitKey);
    setMark('refused');
    setConfirming(false);
    showSaveFailedToast();
  };
  const waitUntil = (deadline: number) => {
    clearTimeout(confirmTimer.current);
    setConfirming(true);
    confirmTimer.current = setTimeout(() => {
      // Settled by the cell now showing this ticket, if it moved.
      if (waitKey && !waitingSaves.has(waitKey)) { setConfirming(false); return; }
      giveUp();
    }, Math.max(0, deadline - Date.now()));
  };

  /** Shows the green, `elapsed` ms into it, and takes it off when it has run. */
  const runGreen = (elapsed: number) => {
    clearTimeout(timer.current);
    setGreenOffset(elapsed);
    setMark('saved');
    timer.current = setTimeout(() => setMark(current => (current === 'saved' ? null : current)), SAVED_MARK_MS - elapsed);
  };

  // A new row in this cell: its own marks go - but a green this ticket's field still has carries on here - and a wait
  // this ticket's field still has is taken up.
  useEffect(() => {
    const elapsed = greenElapsed(waitKey);
    if (elapsed !== undefined) runGreen(elapsed);
    else { clearTimeout(timer.current); setMark(null); }
    clearTimeout(confirmTimer.current);
    const waiting = waitKey ? waitingSaves.get(waitKey) : undefined;
    if (!waiting) { setConfirming(false); return; }
    startValue.current = waiting.start;
    waitUntil(waiting.deadline);
  }, [rowKey]);
  useEffect(() => () => { clearTimeout(timer.current); clearTimeout(confirmTimer.current); }, []);
  useEffect(() => {
    const listener = () => lastEditedChanged(n => n + 1);
    lastEditedListeners.add(listener);
    return () => { lastEditedListeners.delete(listener); };
  }, []);
  // A new save takes the last one's fading green off first - both are animations, and only one can run.
  useEffect(() => {
    if (!saving || confirming) return;
    if (!begun.current) startValue.current = rowValue;
    if (waitKey) greenSince.delete(waitKey);
    setMark(current => (current === 'saved' ? null : current));
  }, [saving]);

  const showSaved = () => {
    if (waitKey) greenSince.set(waitKey, Date.now());
    runGreen(0);
  };

  useEffect(() => {
    if (!confirming || rowValue === startValue.current) return;
    clearTimeout(confirmTimer.current);
    if (waitKey) waitingSaves.delete(waitKey);
    // The mark before busy ends, here and below: a panel passes both on to its linked cells (InlineAssignmentCell).
    showSaved();
    setLastEdited(waitKey);
    setConfirming(false);
  }, [confirming, rowValue]);

  /** `edited` false: green here, but the box that stays goes on another cell the same save changed. */
  const show = (outcome: SaveOutcome, edited = true) => {
    begun.current = false;
    clearTimeout(timer.current);
    if (outcome === 'saved') {
      if (edited) setLastEdited(waitKey);
      showSaved();
    }
    else if (outcome === 'failed') setMark('failed');
    else if (outcome === 'refused' || outcome === 'partial') setMark('refused');
    else if (outcome === 'unconfirmed') {
      const deadline = Date.now() + CONFIRM_MS;
      if (waitKey) waitingSaves.set(waitKey, { start: startValue.current, deadline });
      waitUntil(deadline);
    }
  };
  /** Opening the cell again: a refusal's red has said what it had to. */
  const opening = () => {
    setMark(current => (current === 'refused' ? null : current));
    setLastEdited(null);
  };
  /**
   * For a cell that puts its new value into the row as its save starts (a panel - see InlineAssignmentCell): notes
   * the row's value first, so a timed-out save is still judged against what the row held before.
   */
  const begin = () => { startValue.current = rowValueRef.current; begun.current = true; };
  /** Shows another cell's result on this one - a value this cell shows was saved from there. Red here means look. */
  const mirror = (other: SaveMark) => {
    if (other === 'saved') showSaved();
    else if (other) setMark('refused');
  };

  /** For the element that carries markClass: a green picked up part-way carries on rather than starting again. */
  const markStyle: React.CSSProperties | undefined =
    mark === 'saved' && greenOffset > 0 ? { animationDelay: `-${greenOffset}ms` } : undefined;

  return { mark, markStyle, show, opening, begin, mirror, busy: saving || confirming, edited: isLastEdited(waitKey) };
};

/**
 * setTimeout for a cell, cleared when the cell unmounts or calls clear() - as a new ticket arrives in it, since the
 * lists reuse a row's cells by position. A timer left running fired on a cell already gone, or on another ticket.
 */
export const useCellTimers = () => {
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const clear = () => { timers.current.forEach(clearTimeout); timers.current.clear(); };
  useEffect(() => clear, []);
  const later = (run: () => void, ms: number) => {
    const id = setTimeout(() => { timers.current.delete(id); run(); }, ms);
    timers.current.add(id);
  };
  return { later, clear };
};

/**
 * A panel draws the row's own value, so its green waits for the reloaded row - green round the old value would
 * say the wrong thing. Saving stays shown until then; a row that never changes (an automation put the value
 * back) lets go after 5s. `rowKey` is the ticket: a reload that hands the cell another ticket ends the wait, as that
 * ticket's different value says nothing about this save.
 */
export const useGreenOnReload = (rowKey: number | string, rowValue: unknown, saveMark: { show: (outcome: SaveOutcome) => void }) => {
  const [waiting, setWaiting] = useState(false);
  const waitingRef = useRef(false);
  const valueRef = useRef(rowValue);
  valueRef.current = rowValue;
  const startRef = useRef<unknown>(undefined);
  const timers = useCellTimers();
  const arrived = () => {
    if (!waitingRef.current) return;
    waitingRef.current = false;
    timers.clear();
    setWaiting(false);
    saveMark.show('saved');
  };
  // Before the effect below, which runs in the same commit when a new ticket brings a new value.
  useEffect(() => { waitingRef.current = false; timers.clear(); setWaiting(false); }, [rowKey]);
  useEffect(arrived, [rowValue]);
  /** Notes the row's value as a save starts. */
  const begin = () => { startRef.current = valueRef.current; };
  /**
   * After a save: green now if the row already shows the new value - the ticket list puts it in during the save
   * (_patchRow), before this could start waiting, and the reload brings the same value, so waiting for a change
   * held the grey for the 5s fallback (measured on List Multiples). Otherwise wait for the row to change.
   */
  const wait = () => {
    if (valueRef.current !== startRef.current) { saveMark.show('saved'); return; }
    waitingRef.current = true;
    setWaiting(true);
    timers.later(arrived, 5000);
  };
  return { waiting, begin, wait };
};

/** Keys that move between cells - everything else would open or edit the cell. */
const MOVING_KEYS = new Set(['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'Escape', 'Shift', 'Control', 'Alt', 'Meta']);

const holdEvent = (event: React.SyntheticEvent) => { event.preventDefault(); event.stopPropagation(); };

/**
 * For a cell's resting element while its save is out: the pulse (.inline-saving in main.css), and clicks and
 * editing keys stopped before the cell's own handlers, so a second save of the same field cannot start while the
 * first is unanswered - their answers could land in either order. Keys that move on still work.
 */
export const savingCell = (busy: boolean) => (busy
  ? {
    className: ' inline-saving',
    props: {
      'aria-busy': true,
      onClickCapture: holdEvent,
      onKeyDownCapture: (event: React.KeyboardEvent) => { if (!MOVING_KEYS.has(event.key)) holdEvent(event); }
    }
  }
  : { className: '', props: {} });

/** The toasts' red (react-toast-notify.css), for an editor reopened after its save failed - matches .inline-mark-error. */
export const SAVE_ERROR_COLOR = '#e74c3c';

/** Array.sort's own order (UTF-16 code units), for keys that only have to sort the same way each time. */
export const byCodeUnit = (a: string, b: string): number => {
  if (a < b) return -1;
  return a > b ? 1 : 0;
};

/**
 * The class for a cell's mark - the box, in the toasts' green and red, is drawn by .inline-mark-* in main.css. With no
 * mark, `edited` (useSaveMark) gives the last saved cell its quiet box; while it is green, its green settles into that
 * box rather than fading away.
 */
export const markClass = (mark: SaveMark, edited = false): string => {
  if (mark == null) return edited ? ' inline-mark-edited' : '';
  if (mark === 'saved') return edited ? ' inline-mark-saved inline-mark-settles' : ' inline-mark-saved';
  return ' inline-mark-error';
};

/**
 * Whether a panel shared by several cells (Team / Group / Assignee) shows its green on the cell it was opened from:
 * when that cell's own value changed, or when none of the changed cells is in the view - a view can leave Group out -
 * so every save shows its outcome somewhere. The changed cells that are in the view show it either way.
 */
export const marksOpeningCell = <T>(changed: T[], opener: T, isShown: (column: T) => boolean): boolean =>
  changed.includes(opener) || !changed.some(other => other !== opener && isShown(other));

/** The Preferences switch for editing in the views' cells, kept in the browser like the other preferences. */
export const CELL_EDITING_KEY = 'TikitViewsCellEditing';

/** Whether this agent has cell editing on - on unless switched off in Preferences, or if the browser will not say. */
export const cellEditingOn = (): boolean => {
  try {
    return (localStorage.getItem(CELL_EDITING_KEY) ?? 'true') === 'true';
  } catch {
    return true;
  }
};

/**
 * Why a save to a ticket is refused, as a translation key, from the ticket as the server holds it now - the row may
 * have gone stale since the list loaded. The ticket page's rules: a closed ticket keeps only its status editable,
 * which reopens it, and a merged ticket's status cannot change. Merging also closes a ticket (RelateTickets), so the
 * closed rule covers a merged ticket's other fields. Deflected is not asked: only TVA makes a ticket deflected, at
 * creation, so a row that loaded otherwise cannot become one.
 */
export const ticketSaveRefusal = (ticket: { Closed?: boolean; IsMerged?: boolean } | null | undefined, statusChange: boolean): string | null => {
  if (statusChange && ticket?.IsMerged) return 'ticket.ticket-board.merged-ticket-status-cannot-change';
  if (!statusChange && ticket?.Closed) return 'ticket.ticket-board.closed-ticket-cannot-update';
  return null;
};

/** What a ticket's required custom properties need, as `currentState.customProperties` holds them. */
export interface RequiredPropertySetting {
  PropertyId: string;
  Name: string;
  DataType?: string;
  IsRequired?: boolean;
  IsHidden?: boolean;
  IsArchived?: boolean;
}

/** The required custom properties a ticket has no answer for, the True/False ones apart - they must be True. */
export interface MissingRequiredProperties {
  fields: string[];
  mustBeTrue: string[];
}

/**
 * The required custom properties a ticket has no answer for, by the ticket page's rule (EditTicketForm): archived and
 * hidden ones are not asked for, and a required True/False counts only when True, as its box must be ticked. While any
 * is missing the ticket page saves nothing, so a cell does not either - except the cell filling one of them in, or two
 * missing ones could never be filled (Shaun, 2026-10-20).
 */
export const missingRequiredProperties = (
  answerJson: string | null | undefined, properties: RequiredPropertySetting[] | null | undefined, editingPropertyId?: string
): MissingRequiredProperties => {
  let answers: Record<string, unknown> = {};
  try {
    answers = answerJson ? parseJsonRecovering(answerJson) ?? {} : {};
  } catch {
    answers = {};
  }
  const missing = (properties ?? []).filter(p => p.IsRequired && !p.IsHidden && !p.IsArchived && !answers[p.PropertyId]);
  if (missing.some(p => p.PropertyId === editingPropertyId)) return { fields: [], mustBeTrue: [] };
  const isBoolean = (p: RequiredPropertySetting) => CustomPropertyDataType_LABELS[p.DataType] === CustomPropertyDataType_LABELS.BOOL;
  return {
    fields: missing.filter(p => !isBoolean(p)).map(p => p.Name),
    mustBeTrue: missing.filter(isBoolean).map(p => p.Name)
  };
};

/** The refusal for a save while required custom properties are missing, or null when none is. */
export const missingRequiredMessage = ({ fields, mustBeTrue }: MissingRequiredProperties): string | null => {
  const parts = [
    fields.length > 0 ? i18next.t('ticket.ticket-board.required-fields-first', { names: fields.join(', ') }) : null,
    mustBeTrue.length > 0 ? i18next.t('ticket.ticket-board.set-true-first', { names: mustBeTrue.join(', ') }) : null
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
};
